import { useContext, useEffect, useState } from "preact/hooks";
import ThemeToggle from "../globalComponents/ThemeToggle";
import { AuthContext } from "../../context/AuthContext";
import { useServiceWorkerContext } from "../../context/ServiceWorkerContext";
import {
  deletePostRequestFromStore,
  getAllPostRequests,
  getPostRequestCount,
} from "../../db";
import { Box, Button, Menu, MenuItem, Stack } from "@mui/material";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import StatusMode from "./StatusMode";
import { Link } from "preact-router/match";
import Alert from "../../mui/components/alert/Alert";

const Navbar = ({ vesselName, theme }) => {
  const { logout } = useContext(AuthContext);
  const { updateCount } = useServiceWorkerContext();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const {
    state: { count },
    updateNetworkMode,
  } = useServiceWorkerContext();

  useEffect(() => {
    function handleOnline() {
      setIsOnline(true);
      updateNetworkMode(true);
    }

    function handleOffline() {
      setIsOnline(false);
      updateNetworkMode(false);
    }

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    theme === "dark"
      ? document.body.classList.add("bg-dark")
      : document.body.classList.remove("bg-dark");
  }, [theme]);

  const [requestSuccessMessage, setRequestSuccessMessage] = useState(null);
  const [requestFailedMessage, setRequestFailedMessage] = useState(null);

  const sendAllRequestAfterReconnect = async () => {
    const requests = await getAllPostRequests();
    if (requests.length === 0) {
      return;
    }

    for (const req of requests) {
      try {
        const response = await fetch(req.url, {
          method: req.method,
          body: req.body,
          headers: req.headers,
        });

        if (response.ok) {
          setRequestSuccessMessage("Request sent successfully!");
        } else {
          setRequestFailedMessage("Request failed!");
        }

        await deletePostRequestFromStore(req.id);

        const c = await getPostRequestCount();
        if (c > 0) {
          updateCount(c);
        } else {
          setTimeout(() => updateCount(c), 3000);
        }
      } catch (error) {
        setRequestFailedMessage(`Error sending request! ${error.message}`);
      }
    }
  };

  useEffect(() => {
    sendAllRequestAfterReconnect();
  }, [isOnline]);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  return (
    <Box>
      <Alert
        severity="error"
        message={requestFailedMessage}
        onClose={() => setRequestFailedMessage(null)}
      />
      <Alert
        severity="success"
        message={requestSuccessMessage}
        onClose={() => setRequestSuccessMessage(null)}
      />
      <Box
        sx={{
          position: "fixed",
          zIndex: 10,
          backgroundColor: "layout.navbar.background",
          width: "100%",
          height: "50px",
          top: 0,
        }}
      >
        <Stack flexDirection="row" height="100%" justifyContent="space-between">
          {/*LEFT SIDE*/}
          <Stack
            flexDirection="row"
            gap="20px"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Stack
              flexDirection="row"
              gap="20px"
              justifyContent="flex-start"
              alignItems="center"
              p="14px"
            >
              <Logo />
              <Stack
                justifyContent="center"
                alignItems="center"
                p="0 5px"
                sx={{
                  backgroundColor: "layout.navbar.badge.background",
                  color: "layout.navbar.badge.color",
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "24px",
                  letterSpacing: "3px",
                  borderRadius: "5px",
                }}
              >
                ON BOARD
              </Stack>
            </Stack>
            <Stack
              flexDirection="row"
              justifyContent="center"
              alignItems="center"
              gap="10px"
              sx={{ height: "100%", paddingTop: "10px" }}
            >
              <Stack
                flexDirection="row"
                gap="15px"
                justifyContent="center"
                alignItems="center"
                sx={{
                  height: "100%",
                  "& a": {
                    padding: "0 13px 0 10px",
                    borderTopLeftRadius: "5px",
                    borderTopRightRadius: "5px",
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: "16.94px",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "18px",
                    textDecoration: "none",
                    // backgroundColor: 'layout.navbar.navLink.default.background',
                    color: "layout.navbar.navLink.default.color",
                  },
                  "& a.active": {
                    backgroundColor: "layout.navbar.navLink.active.background",
                    color: "layout.navbar.navLink.active.color",
                  },
                }}
              >
                <Link href="/scheduled" activeClassName="active">
                  <DashboardRoundedIcon /> Scheduled
                </Link>
              </Stack>
              <Stack
                flexDirection="row"
                gap="15px"
                justifyContent="center"
                alignItems="center"
                sx={{
                  height: "100%",
                  "& a": {
                    padding: "0 13px 0 10px",
                    borderTopLeftRadius: "5px",
                    borderTopRightRadius: "5px",
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: "16.94px",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "18px",
                    textDecoration: "none",
                    // backgroundColor: 'layout.navbar.navLink.default.background',
                    color: "layout.navbar.navLink.default.color",
                  },
                  "& a.active": {
                    backgroundColor: "layout.navbar.navLink.active.background",
                    color: "layout.navbar.navLink.active.color",
                  },
                }}
              >
                <Link href="/archive" activeClassName="active">
                  <ShowChartRoundedIcon /> Archive
                </Link>
              </Stack>
            </Stack>
          </Stack>
          {/*RIGHT SIDE*/}
          <Stack
            flexDirection="row"
            justifyContent="flex-end"
            alignItems="center"
            gap="30px"
            p="0 20px 0 0"
          >
            <StatusMode isOnline={isOnline} count={count} />
            <ThemeToggle
              sxFromProps={{ color: "layout.navbar.toogleMode.color" }}
            />
            <>
              <Button
                variant="text"
                sx={{
                  color: "layout.navbar.profile.color",
                  fontWeight: 500,
                  fontSize: "16px",
                  lineHeight: "19.36px",
                  textTransform: "capitalize",
                  paddingLeft: "0",
                }}
                id="basic-button"
                aria-controls={open ? "basic-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
              >
                <AccountCircleRoundedIcon
                  sx={{
                    color: "layout.navbar.profile.icon",
                    marginRight: "9px",
                  }}
                />
                {vesselName}
              </Button>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
                sx={{
                  "& .MuiPaper-root": {
                    backgroundColor: "layout.navbar.dropdown.background",
                    color: "layout.navbar.dropdown.color",
                    border: "1px solid",
                    borderColor: "layout.navbar.dropdown.border",
                  },
                  "& .MuiMenuItem-root": {
                    "&:hover": {
                      backgroundColor: "layout.navbar.dropdown.hover",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#bbb",
                      color: "#fff",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "#aaa",
                    },
                  },
                }}
              >
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
            </>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};
export default Navbar;

function Logo() {
  return (
    <svg
      width="124"
      height="32"
      viewBox="0 0 124 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0.957431 16.9891H5.83724L1.11765 21.7968C1.02113 21.2128 0.957431 20.5954 0.957431 20.0094V16.9872V16.9891ZM19.581 27.6446L18.3379 22.0249L22.6116 17.6714V20.0114C22.6116 22.9668 21.4631 25.6646 19.581 27.6466M9.69592 30.8281L17.6044 22.8036L18.8166 28.3919C16.9346 30.0495 14.4792 31.0562 11.8 31.0562C11.0664 31.0228 10.3657 30.9579 9.69592 30.8281ZM4.11348 26.9623L3.73128 27.3517C2.61556 26.0854 1.78553 24.5575 1.33963 22.8684L6.05923 18.0608L4.11348 26.9623ZM5.29483 25.7275L7.20776 16.9891H13.8731L5.29483 25.7275ZM17.2531 16.9891H21.9727L18.114 20.9198L17.2531 16.9891ZM17.3496 21.7004L8.64391 30.5686C7.17688 30.1144 5.87006 29.3672 4.72153 28.3604L4.91263 27.4185L15.149 16.9911H16.2648L17.3496 21.7024V21.7004ZM0 20.0094C0 26.6359 5.29483 31.9981 11.7691 31.9981C18.2433 31.9981 23.5381 26.6045 23.5381 20.0094V16.0453H0V20.0094Z"
        fill="#00B8B2"
      />
      <path
        d="M6.60164 6.10544C6.69816 3.2464 8.99329 0.939904 11.8328 0.939904C14.6722 0.939904 16.9674 3.2464 17.0639 6.10544H6.60357H6.60164ZM12.2787 8.11896H11.2575V7.07878H12.2787V8.11896ZM23.569 13.0249V10.0696H17.1585V7.08074H17.9557V6.95096C17.9885 6.72287 17.9885 6.5282 17.9885 6.30208C17.9885 2.82561 15.2456 0 11.8328 0C8.41999 0 5.67703 2.82561 5.67703 6.27062C5.67703 6.49871 5.67703 6.72484 5.70984 6.9195V7.04928H6.37966V10.0715H0V13.0269H10.3329V14.0671H0V15.0424H11.2575V12.087H0.957431V11.0468H11.2904V10.0715H7.33516V7.08271H10.3329V9.06477H13.2342V7.08271H16.2011V10.0715H12.2787V11.0468H22.6116V12.087H12.2787V15.0424H23.569V14.0671H13.2033V13.0269H23.569V13.0249Z"
        fill="#00B8B2"
      />
      <path
        d="M95.6858 16.5386L94.1671 12.4071C94.33 13.4264 94.4099 14.9433 94.4099 16.9608V22.6424H92.681V9.68999H94.5579L96.9055 15.7545C97.2312 16.5959 97.5183 17.3619 97.7611 18.0525C98.0039 18.7401 98.1726 19.2195 98.2585 19.4849L98.3887 19.889C98.2525 19.0567 98.1815 17.5278 98.1815 15.2961V9.68999H99.8897V22.6424H98.0305L95.6829 16.5386H95.6858ZM85.4279 12.6936C85.4279 11.623 84.8714 11.0862 83.7582 11.0862C82.6451 11.0862 82.1033 11.623 82.1033 12.6936V19.6387C82.1033 20.6972 82.6569 21.228 83.7582 21.228C84.8595 21.228 85.4279 20.6972 85.4279 19.6387V12.6936ZM87.1568 19.693C87.1568 20.6399 86.8667 21.3999 86.2924 21.9729C85.718 22.5458 84.8714 22.8323 83.7582 22.8323C82.6451 22.8323 81.7984 22.5458 81.23 21.9729C80.6616 21.3999 80.3774 20.6399 80.3774 19.693V12.6333C80.3774 11.6924 80.6616 10.9294 81.23 10.3595C81.7955 9.78649 82.6392 9.5 83.7582 9.5C84.8773 9.5 85.7151 9.78649 86.2924 10.3595C86.8667 10.9325 87.1568 11.6924 87.1568 12.6333V19.693ZM75.1878 18.5682V19.693C75.1878 20.6399 74.9006 21.3999 74.3263 21.9729C73.749 22.5458 72.9023 22.8323 71.7862 22.8323C70.6701 22.8323 69.8323 22.5458 69.261 21.9729C68.6926 21.3999 68.4084 20.6399 68.4084 19.693V12.6333C68.4084 11.6924 68.6926 10.9294 69.261 10.3595C69.8294 9.78649 70.6731 9.5 71.7862 9.5C72.8993 9.5 73.746 9.78649 74.3263 10.3595C74.9006 10.9325 75.1878 11.6924 75.1878 12.6333V13.8034H73.4589V12.6936C73.4589 11.623 72.9023 11.0862 71.7862 11.0862C70.6701 11.0862 70.1343 11.623 70.1343 12.6936V19.6387C70.1343 20.6972 70.6879 21.228 71.7862 21.228C72.8845 21.228 73.4589 20.6972 73.4589 19.6387V18.5682H75.1878ZM61.0428 17.6665L60.726 16.1556C60.202 13.6315 59.906 12.009 59.8438 11.2943C59.7698 12.1357 59.4767 13.7581 58.9793 16.1556L58.6596 17.6665H61.0428ZM61.3832 19.1984H58.3192L57.585 22.6424H55.859L58.6922 9.68999H61.0043L63.8374 22.6424H62.1085L61.3773 19.1984H61.3832ZM51.8358 22.6424H46.0511V9.68999H51.8358V11.222H47.78V15.3353H51.3118V16.8673H47.78V21.0953H51.8358V22.6424ZM38.8188 18.4506C38.8188 17.4071 38.2592 16.8824 37.1461 16.8824H35.4735V21.0923H37.2024C38.28 21.0923 38.8158 20.5706 38.8158 19.5241V18.4506H38.8188ZM35.4735 11.2189V15.3323H37.0336C38.1467 15.3323 38.7063 14.8106 38.7063 13.7642V12.7871C38.7063 11.7437 38.1675 11.2189 37.0928 11.2189H35.4764H35.4735ZM40.4144 13.6707C40.4144 14.883 39.9645 15.6852 39.0645 16.0832C40.0414 16.427 40.5269 17.2322 40.5269 18.4928V19.6206C40.5269 20.5645 40.2605 21.3064 39.7276 21.8432C39.1947 22.377 38.3747 22.6454 37.2616 22.6454H33.7505V9.693H37.1491C38.2622 9.693 39.0882 9.9614 39.6181 10.4982C40.151 11.032 40.4174 11.7738 40.4174 12.7177V13.6737L40.4144 13.6707Z"
        fill="white"
      />
      <path
        d="M107.35 17V17.1508H105.639V9.67188H111.758V11.2069H107.35V14.7744C107.738 14.313 108.345 14.0838 109.159 14.0838C110.139 14.0838 110.867 14.3522 111.355 14.889C111.835 15.4258 112.078 16.1677 112.078 17.1146V19.7443C112.078 20.7032 111.811 21.4632 111.278 22.0151C110.745 22.573 109.937 22.8534 108.857 22.8534C107.776 22.8534 106.971 22.573 106.438 22.0151C105.902 21.4602 105.639 20.7032 105.639 19.7443V19.2075H107.35V19.702C107.35 20.7515 107.85 21.2762 108.857 21.2762C109.863 21.2762 110.366 20.7515 110.366 19.702V17.1508C110.366 16.1043 109.863 15.5796 108.857 15.5796C107.85 15.5796 107.376 16.0531 107.35 17Z"
        fill="white"
      />
      <path
        d="M119.973 17.8655C119.42 18.2364 119.065 18.5591 118.908 18.8336C118.754 19.108 118.674 19.5151 118.674 20.0519V21.1074H123.23V22.6605H116.96V19.9916C116.96 19.1984 117.099 18.5652 117.374 18.0947C117.652 17.6212 118.111 17.1719 118.748 16.7497L120.574 15.5012C120.927 15.275 121.158 15.0217 121.273 14.7533C121.386 14.4849 121.451 14.0989 121.463 13.6013V12.7208C121.463 11.6713 120.968 11.1466 119.973 11.1466C119.432 11.1466 119.053 11.2793 118.825 11.5416C118.597 11.804 118.484 12.199 118.484 12.7238V13.737H116.752V12.6816C116.752 11.7226 117.019 10.9656 117.552 10.4107C118.088 9.84983 118.893 9.57239 119.973 9.57239C121.054 9.57239 121.838 9.84983 122.374 10.4107C122.907 10.9686 123.174 11.7226 123.174 12.6816V13.7582C123.162 14.4759 123.049 15.0519 122.836 15.4952C122.62 15.9355 122.259 16.3305 121.744 16.6743L119.973 17.8655Z"
        fill="white"
      />
    </svg>
  );
}
